import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect, Link, useParams } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  GripVertical,
  CheckCircle,
  AlertCircle,
  Save,
  ArrowLeft,
  ImageIcon,
  Music,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// ─── Types ───────────────────────────────────────────────────────────────────

type QuestionType =
  | "free_text"
  | "multiple_choice"
  | "true_false"
  | "number"
  | "picture"
  | "picture_round"
  | "audio"
  | "multi_part"
  | "closest_wins";

interface QuizOption {
  id?: number;
  optionText: string;
  isCorrect: boolean;
  sortOrder: number;
}

interface AcceptedAnswer {
  id?: number;
  answerText: string;
}

interface PictureRoundImage {
  id?: number;
  imageNumber: number;
  imageUrl: string;
  correctAnswer: string;
  sortOrder: number;
}

interface Question {
  id?: number;
  roundId?: number;
  quizId?: number;
  questionNumber: number;
  type: QuestionType;
  questionText: string;
  imageUrl: string | null;
  audioUrl: string | null;
  correctAnswer: string | null;
  numberAnswer: number | null;
  numberTolerance: number | null;
  points: number;
  timerSeconds: number | null;
  noTimer: boolean;
  doublePoints: boolean;
  caseSensitive: boolean;
  ignorePunctuation: boolean;
  autoMark: boolean;
  revealAfterLock: boolean;
  hostNotes: string | null;
  explanation: string | null;
  randomiseOptions: boolean;
  fastestAnswerBonus: boolean;
  options?: QuizOption[];
  acceptedAnswers?: AcceptedAnswer[];
  pictureRoundImages?: PictureRoundImage[];
}

interface Round {
  id?: number;
  quizId?: number;
  title: string;
  roundNumber: number;
  description: string | null;
  defaultPoints: number;
  defaultTimerSeconds: number | null;
  showScores: boolean;
  questions: Question[];
}

interface QuizData {
  id?: number;
  title: string;
  description: string | null;
  hostName: string | null;
  venue: string | null;
  scheduledAt: string | null;
  joinCode: string | null;
  joinInstructions: string | null;
  maxTeamSize: number | null;
  maxTeams: number | null;
  allowLateJoining: boolean;
  showScoresBetweenRounds: boolean;
  autoLockAnswers: boolean;
  allowAnswerChanges: boolean;
  defaultTimerSeconds: number;
  status: string;
  rounds: Round[];
}

interface ValidationIssue {
  type: "error" | "warning";
  message: string;
  roundId?: number;
  questionId?: number;
}

// ─── Defaults ────────────────────────────────────────────────────────────────

function defaultQuestion(roundId: number, num: number): Question {
  return {
    roundId,
    questionNumber: num,
    type: "free_text",
    questionText: "",
    imageUrl: null,
    audioUrl: null,
    correctAnswer: null,
    numberAnswer: null,
    numberTolerance: null,
    points: 1,
    timerSeconds: null,
    noTimer: false,
    doublePoints: false,
    caseSensitive: false,
    ignorePunctuation: true,
    autoMark: false,
    revealAfterLock: true,
    hostNotes: null,
    explanation: null,
    randomiseOptions: false,
    fastestAnswerBonus: false,
    options: [
      { optionText: "", isCorrect: true, sortOrder: 0 },
      { optionText: "", isCorrect: false, sortOrder: 1 },
      { optionText: "", isCorrect: false, sortOrder: 2 },
      { optionText: "", isCorrect: false, sortOrder: 3 },
    ],
    acceptedAnswers: [],
    pictureRoundImages: [],
  };
}

function defaultRound(num: number): Round {
  return {
    title: `Round ${num}`,
    roundNumber: num,
    description: null,
    defaultPoints: 1,
    defaultTimerSeconds: 30,
    showScores: true,
    questions: [],
  };
}

function defaultQuiz(): QuizData {
  return {
    title: "",
    description: null,
    hostName: null,
    venue: null,
    scheduledAt: null,
    joinCode: null,
    joinInstructions: null,
    maxTeamSize: 6,
    maxTeams: null,
    allowLateJoining: true,
    showScoresBetweenRounds: true,
    autoLockAnswers: true,
    allowAnswerChanges: true,
    defaultTimerSeconds: 30,
    status: "draft",
    rounds: [],
  };
}

const TIMER_OPTIONS = [
  { label: "10s", value: 10 },
  { label: "20s", value: 20 },
  { label: "30s", value: 30 },
  { label: "45s", value: 45 },
  { label: "60s", value: 60 },
  { label: "90s", value: 90 },
  { label: "120s", value: 120 },
];

const QUESTION_TYPES: { value: QuestionType; label: string }[] = [
  { value: "free_text",       label: "Free Text" },
  { value: "multiple_choice", label: "Multiple Choice" },
  { value: "true_false",      label: "True / False" },
  { value: "number",          label: "Number" },
  { value: "picture",         label: "Picture" },
  { value: "picture_round",   label: "Picture Round" },
  { value: "audio",           label: "Audio" },
  { value: "multi_part",      label: "Multi-Part" },
  { value: "closest_wins",    label: "Closest Wins" },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}

function SwitchRow({ label, checked, onCheckedChange, description }: {
  label: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  description?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <div>
        <p className="text-sm font-medium">{label}</p>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

// ─── Question Editor ──────────────────────────────────────────────────────────

function QuestionEditor({
  question,
  onChange,
}: {
  question: Question;
  onChange: (q: Question) => void;
}) {
  const token = localStorage.getItem("ayrshire_token");
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  function set<K extends keyof Question>(key: K, value: Question[K]) {
    onChange({ ...question, [key]: value });
  }

  async function uploadImage(file: File): Promise<string> {
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch("/api/storage/upload", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!r.ok) throw new Error("Upload failed");
    const d = await r.json() as { url: string };
    return d.url;
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      set("imageUrl", url);
    } catch {
      toast({ title: "Image upload failed", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  }

  function updateOption(idx: number, key: keyof QuizOption, value: string | boolean | number) {
    const opts = [...(question.options ?? [])];
    opts[idx] = { ...opts[idx], [key]: value };
    // If setting isCorrect for mc, clear others
    if (key === "isCorrect" && value === true && question.type === "multiple_choice") {
      opts.forEach((o, i) => { if (i !== idx) opts[i] = { ...o, isCorrect: false }; });
    }
    set("options", opts);
  }

  function addOption() {
    const opts = [...(question.options ?? [])];
    opts.push({ optionText: "", isCorrect: false, sortOrder: opts.length });
    set("options", opts);
  }

  function removeOption(idx: number) {
    const opts = (question.options ?? []).filter((_, i) => i !== idx);
    set("options", opts);
  }

  function addAcceptedAnswer() {
    set("acceptedAnswers", [...(question.acceptedAnswers ?? []), { answerText: "" }]);
  }

  function updateAcceptedAnswer(idx: number, text: string) {
    const aa = [...(question.acceptedAnswers ?? [])];
    aa[idx] = { ...aa[idx], answerText: text };
    set("acceptedAnswers", aa);
  }

  function removeAcceptedAnswer(idx: number) {
    set("acceptedAnswers", (question.acceptedAnswers ?? []).filter((_, i) => i !== idx));
  }

  function addPictureImage() {
    const imgs = question.pictureRoundImages ?? [];
    set("pictureRoundImages", [
      ...imgs,
      { imageNumber: imgs.length + 1, imageUrl: "", correctAnswer: "", sortOrder: imgs.length },
    ]);
  }

  function updatePictureImage(idx: number, key: keyof PictureRoundImage, value: string | number) {
    const imgs = [...(question.pictureRoundImages ?? [])];
    imgs[idx] = { ...imgs[idx], [key]: value };
    set("pictureRoundImages", imgs);
  }

  async function uploadPictureRoundImage(idx: number, file: File) {
    setUploading(true);
    try {
      const url = await uploadImage(file);
      updatePictureImage(idx, "imageUrl", url);
    } catch {
      toast({ title: "Image upload failed", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  }

  const t = question.type;

  return (
    <div className="space-y-4">
      {/* Type selector */}
      <FieldRow label="Question Type">
        <Select value={t} onValueChange={(v) => set("type", v as QuestionType)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {QUESTION_TYPES.map(qt => (
              <SelectItem key={qt.value} value={qt.value}>{qt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>

      {/* Question text */}
      <FieldRow label="Question Text">
        <Textarea
          value={question.questionText}
          onChange={e => set("questionText", e.target.value)}
          placeholder="Enter your question..."
          rows={3}
        />
      </FieldRow>

      {/* Type-specific fields */}
      {(t === "free_text" || t === "picture") && (
        <>
          {t === "picture" && (
            <FieldRow label="Image">
              {question.imageUrl && (
                <img src={question.imageUrl} alt="" className="w-full max-h-40 object-contain rounded border mb-2" />
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <Button type="button" variant="outline" size="sm" className="gap-1" disabled={uploading} asChild>
                  <span><ImageIcon className="h-4 w-4" />{uploading ? "Uploading..." : "Upload Image"}</span>
                </Button>
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              </label>
              {question.imageUrl && (
                <Input
                  value={question.imageUrl}
                  onChange={e => set("imageUrl", e.target.value)}
                  placeholder="Or enter image URL"
                  className="mt-1"
                />
              )}
            </FieldRow>
          )}
          <FieldRow label="Correct Answer">
            <Input
              value={question.correctAnswer ?? ""}
              onChange={e => set("correctAnswer", e.target.value)}
              placeholder="Correct answer"
            />
          </FieldRow>
          <FieldRow label="Also accept these answers">
            {(question.acceptedAnswers ?? []).map((aa, i) => (
              <div key={i} className="flex gap-2 mb-1">
                <Input
                  value={aa.answerText}
                  onChange={e => updateAcceptedAnswer(i, e.target.value)}
                  placeholder="Alternative answer"
                />
                <Button type="button" variant="ghost" size="icon" onClick={() => removeAcceptedAnswer(i)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addAcceptedAnswer} className="gap-1">
              <Plus className="h-3.5 w-3.5" /> Add alternative
            </Button>
          </FieldRow>
          <div className="space-y-2">
            <SwitchRow label="Case sensitive" checked={question.caseSensitive} onCheckedChange={v => set("caseSensitive", v)} />
            <SwitchRow label="Ignore punctuation" checked={question.ignorePunctuation} onCheckedChange={v => set("ignorePunctuation", v)} />
          </div>
        </>
      )}

      {t === "multiple_choice" && (
        <>
          <FieldRow label="Options (select correct answer)">
            <div className="space-y-2">
              {(question.options ?? []).map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="mc-correct"
                    checked={opt.isCorrect}
                    onChange={() => updateOption(i, "isCorrect", true)}
                    className="h-4 w-4 shrink-0"
                  />
                  <Input
                    value={opt.optionText}
                    onChange={e => updateOption(i, "optionText", e.target.value)}
                    placeholder={`Option ${String.fromCharCode(65 + i)}`}
                    className="flex-1"
                  />
                  {(question.options?.length ?? 0) > 2 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeOption(i)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              {(question.options?.length ?? 0) < 6 && (
                <Button type="button" variant="outline" size="sm" onClick={addOption} className="gap-1">
                  <Plus className="h-3.5 w-3.5" /> Add option
                </Button>
              )}
            </div>
          </FieldRow>
          <SwitchRow label="Randomise option order" checked={question.randomiseOptions} onCheckedChange={v => set("randomiseOptions", v)} />
        </>
      )}

      {t === "true_false" && (
        <FieldRow label="Correct Answer">
          <div className="flex gap-4">
            {["True", "False"].map(val => (
              <label key={val} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tf-answer"
                  checked={question.correctAnswer === val}
                  onChange={() => set("correctAnswer", val)}
                  className="h-4 w-4"
                />
                <span className="text-sm font-medium">{val}</span>
              </label>
            ))}
          </div>
        </FieldRow>
      )}

      {(t === "number" || t === "closest_wins") && (
        <>
          <FieldRow label="Correct Number">
            <Input
              type="number"
              value={question.numberAnswer ?? ""}
              onChange={e => set("numberAnswer", e.target.value ? Number(e.target.value) : null)}
              placeholder="e.g. 1969"
            />
          </FieldRow>
          {t === "number" && (
            <FieldRow label="Tolerance (±)">
              <Input
                type="number"
                value={question.numberTolerance ?? ""}
                onChange={e => set("numberTolerance", e.target.value ? Number(e.target.value) : null)}
                placeholder="e.g. 5 (accept answers within ±5)"
              />
            </FieldRow>
          )}
          {t === "closest_wins" && (
            <p className="text-xs text-muted-foreground">The team with the closest answer wins. Correct answer is revealed by host.</p>
          )}
        </>
      )}

      {t === "audio" && (
        <>
          <FieldRow label="Audio URL">
            <div className="flex gap-2">
              <Input
                value={question.audioUrl ?? ""}
                onChange={e => set("audioUrl", e.target.value || null)}
                placeholder="Audio file URL"
              />
              <Button type="button" variant="outline" size="icon">
                <Music className="h-4 w-4" />
              </Button>
            </div>
          </FieldRow>
          <FieldRow label="Correct Answer">
            <Input
              value={question.correctAnswer ?? ""}
              onChange={e => set("correctAnswer", e.target.value || null)}
              placeholder="e.g. Song title / Artist"
            />
          </FieldRow>
        </>
      )}

      {t === "picture_round" && (
        <FieldRow label="Images">
          <div className="space-y-3">
            {(question.pictureRoundImages ?? []).map((img, i) => (
              <div key={i} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground">Image {img.imageNumber}</span>
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                    set("pictureRoundImages", (question.pictureRoundImages ?? []).filter((_, j) => j !== i));
                  }}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {img.imageUrl && (
                  <img src={img.imageUrl} alt="" className="w-full max-h-32 object-contain rounded" />
                )}
                <label className="flex items-center gap-2 cursor-pointer">
                  <Button type="button" variant="outline" size="sm" asChild>
                    <span><ImageIcon className="h-3.5 w-3.5 mr-1" />Upload</span>
                  </Button>
                  <input type="file" accept="image/*" className="hidden" onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) uploadPictureRoundImage(i, f);
                  }} />
                </label>
                <Input
                  value={img.imageUrl}
                  onChange={e => updatePictureImage(i, "imageUrl", e.target.value)}
                  placeholder="Or enter image URL"
                />
                <Input
                  value={img.correctAnswer}
                  onChange={e => updatePictureImage(i, "correctAnswer", e.target.value)}
                  placeholder="Correct answer for this image"
                />
              </div>
            ))}
            {(question.pictureRoundImages?.length ?? 0) < 12 && (
              <Button type="button" variant="outline" size="sm" onClick={addPictureImage} className="gap-1">
                <Plus className="h-3.5 w-3.5" /> Add image
              </Button>
            )}
          </div>
        </FieldRow>
      )}

      {t === "multi_part" && (
        <FieldRow label="Parts">
          <div className="space-y-2">
            {(question.acceptedAnswers ?? []).map((part, i) => (
              <div key={i} className="flex gap-2">
                <span className="text-xs font-medium text-muted-foreground w-16 pt-2">Part {i + 1}</span>
                <Input
                  value={part.answerText}
                  onChange={e => updateAcceptedAnswer(i, e.target.value)}
                  placeholder="Correct answer for this part"
                  className="flex-1"
                />
                <Button type="button" variant="ghost" size="icon" onClick={() => removeAcceptedAnswer(i)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addAcceptedAnswer} className="gap-1">
              <Plus className="h-3.5 w-3.5" /> Add part
            </Button>
          </div>
        </FieldRow>
      )}

      {/* Common fields */}
      <div className="border-t pt-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <FieldRow label="Points">
            <Input
              type="number"
              min={1}
              value={question.points}
              onChange={e => set("points", Number(e.target.value) || 1)}
            />
          </FieldRow>
          <FieldRow label="Timer (seconds)">
            <Select
              value={question.noTimer ? "none" : (question.timerSeconds?.toString() ?? "default")}
              onValueChange={v => {
                if (v === "none") {
                  set("noTimer", true);
                  set("timerSeconds", null);
                } else if (v === "default") {
                  set("noTimer", false);
                  set("timerSeconds", null);
                } else {
                  set("noTimer", false);
                  set("timerSeconds", Number(v));
                }
              }}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Use round default</SelectItem>
                <SelectItem value="none">No timer</SelectItem>
                {TIMER_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value.toString()}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldRow>
        </div>
        <div className="space-y-2">
          <SwitchRow label="Double points" checked={question.doublePoints} onCheckedChange={v => set("doublePoints", v)} />
          <SwitchRow label="Fastest answer bonus" checked={question.fastestAnswerBonus} onCheckedChange={v => set("fastestAnswerBonus", v)} />
          <SwitchRow label="Auto-mark" checked={question.autoMark} onCheckedChange={v => set("autoMark", v)} description="Automatically mark correct answers" />
          <SwitchRow label="Reveal answer after lock" checked={question.revealAfterLock} onCheckedChange={v => set("revealAfterLock", v)} />
        </div>
      </div>

      <FieldRow label="Host Notes (private)">
        <Textarea
          value={question.hostNotes ?? ""}
          onChange={e => set("hostNotes", e.target.value || null)}
          placeholder="Notes visible only to the host..."
          rows={2}
        />
      </FieldRow>

      <FieldRow label="Explanation (shown after reveal)">
        <Textarea
          value={question.explanation ?? ""}
          onChange={e => set("explanation", e.target.value || null)}
          placeholder="Interesting fact or context shown after reveal..."
          rows={2}
        />
      </FieldRow>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AdminQuizBuilder() {
  const { user, isLoading: authLoading } = useAuth();
  const params = useParams<{ id?: string }>();
  const quizId = params.id && params.id !== "new" ? Number(params.id) : null;
  const isNew = !quizId;
  const { toast } = useToast();
  const qc = useQueryClient();
  const token = localStorage.getItem("ayrshire_token");

  const [quiz, setQuiz] = useState<QuizData>(defaultQuiz());
  const [selectedRoundIdx, setSelectedRoundIdx] = useState<number | null>(null);
  const [selectedQuestionIdx, setSelectedQuestionIdx] = useState<number | null>(null);
  const [expandedRounds, setExpandedRounds] = useState<Set<number>>(new Set([0]));
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [validation, setValidation] = useState<ValidationIssue[]>([]);
  const [showValidation, setShowValidation] = useState(false);
  const [deleteRoundIdx, setDeleteRoundIdx] = useState<number | null>(null);
  const [deleteQuestionIdx, setDeleteQuestionIdx] = useState<{ roundIdx: number; questionIdx: number } | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedIdRef = useRef<number | null>(quizId);

  // Load existing quiz
  const { data: serverQuiz, isLoading: loadingQuiz } = useQuery<QuizData>({
    queryKey: ["admin-quiz", quizId],
    enabled: !isNew && user?.role === "admin",
    queryFn: () =>
      fetch(`/api/admin/quiz/${quizId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()),
  });

  useEffect(() => {
    if (serverQuiz) {
      setQuiz(serverQuiz);
      savedIdRef.current = serverQuiz.id ?? null;
    }
  }, [serverQuiz]);

  // Save quiz
  const saveMutation = useMutation({
    mutationFn: async (data: QuizData) => {
      const id = savedIdRef.current;
      const url = id ? `/api/admin/quiz/${id}` : "/api/admin/quiz";
      const method = id ? "PUT" : "POST";
      const r = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error("Save failed");
      return r.json() as Promise<QuizData & { id: number }>;
    },
    onSuccess: (saved) => {
      setSaveState("saved");
      savedIdRef.current = saved.id;
      qc.invalidateQueries({ queryKey: ["admin-quizzes"] });
      // Update URL if new quiz
      if (isNew && saved.id) {
        window.history.replaceState({}, "", `/admin/quiz/${saved.id}/edit`);
      }
    },
    onError: () => {
      setSaveState("error");
      toast({ title: "Save failed", variant: "destructive" });
    },
  });

  const debouncedSave = useCallback((data: QuizData) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState("saving");
    saveTimer.current = setTimeout(() => {
      saveMutation.mutate(data);
    }, 2000);
  }, [saveMutation]);

  function updateQuiz(updates: Partial<QuizData>) {
    const next = { ...quiz, ...updates };
    setQuiz(next);
    debouncedSave(next);
  }

  // Round operations
  function addRound() {
    const next = [...quiz.rounds, defaultRound(quiz.rounds.length + 1)];
    updateQuiz({ rounds: next });
    setSelectedRoundIdx(next.length - 1);
    setSelectedQuestionIdx(null);
    setExpandedRounds(prev => { const s = new Set(prev); s.add(next.length - 1); return s; });
  }

  function updateRound(idx: number, updates: Partial<Round>) {
    const rounds = quiz.rounds.map((r, i) => i === idx ? { ...r, ...updates } : r);
    updateQuiz({ rounds });
  }

  function deleteRound(idx: number) {
    const rounds = quiz.rounds.filter((_, i) => i !== idx).map((r, i) => ({
      ...r,
      roundNumber: i + 1,
      title: r.title.startsWith("Round ") ? `Round ${i + 1}` : r.title,
    }));
    updateQuiz({ rounds });
    setDeleteRoundIdx(null);
    if (selectedRoundIdx === idx) {
      setSelectedRoundIdx(null);
      setSelectedQuestionIdx(null);
    }
  }

  // Question operations
  function addQuestion(roundIdx: number) {
    const round = quiz.rounds[roundIdx];
    const newQ = defaultQuestion(round.id ?? 0, round.questions.length + 1);
    const rounds = quiz.rounds.map((r, i) =>
      i === roundIdx ? { ...r, questions: [...r.questions, newQ] } : r
    );
    updateQuiz({ rounds });
    setSelectedRoundIdx(roundIdx);
    setSelectedQuestionIdx(round.questions.length);
  }

  function updateQuestion(roundIdx: number, qIdx: number, q: Question) {
    const rounds = quiz.rounds.map((r, i) =>
      i === roundIdx
        ? { ...r, questions: r.questions.map((q2, j) => j === qIdx ? q : q2) }
        : r
    );
    updateQuiz({ rounds });
  }

  function deleteQuestion(roundIdx: number, qIdx: number) {
    const rounds = quiz.rounds.map((r, i) =>
      i === roundIdx
        ? { ...r, questions: r.questions.filter((_, j) => j !== qIdx).map((q, j) => ({ ...q, questionNumber: j + 1 })) }
        : r
    );
    updateQuiz({ rounds });
    setDeleteQuestionIdx(null);
    if (selectedRoundIdx === roundIdx && selectedQuestionIdx === qIdx) {
      setSelectedQuestionIdx(null);
    }
  }

  // Validate
  async function validate() {
    const id = savedIdRef.current;
    if (!id) {
      toast({ title: "Save the quiz first before validating" });
      return;
    }
    try {
      const r = await fetch(`/api/admin/quiz/${id}/validate`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // API returns { valid, errors: string[], warnings: string[] }
      const data = await r.json() as { valid: boolean; errors: string[]; warnings: string[] };
      const issues: ValidationIssue[] = [
        ...(data.errors ?? []).map((message): ValidationIssue => ({ type: "error", message })),
        ...(data.warnings ?? []).map((message): ValidationIssue => ({ type: "warning", message })),
      ];
      setValidation(issues);
      setShowValidation(true);
    } catch {
      toast({ title: "Validation failed", variant: "destructive" });
    }
  }

  const selectedRound = selectedRoundIdx !== null ? quiz.rounds[selectedRoundIdx] : null;
  const selectedQuestion = selectedRound && selectedQuestionIdx !== null
    ? selectedRound.questions[selectedQuestionIdx]
    : null;

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;
  if (!isNew && loadingQuiz) return (
    <AdminLayout current="/admin/quiz">
      <div className="py-12 text-center text-muted-foreground">Loading quiz...</div>
    </AdminLayout>
  );

  return (
    <AdminLayout current="/admin/quiz">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Link href="/admin/quiz">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-primary truncate">
              {isNew ? "New Quiz" : (quiz.title || "Untitled Quiz")}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {saveState === "saving" && <span className="text-xs text-muted-foreground">Saving...</span>}
            {saveState === "saved" && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle className="h-3.5 w-3.5" /> Saved
              </span>
            )}
            {saveState === "error" && (
              <span className="text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3.5 w-3.5" /> Failed
              </span>
            )}
            <Button variant="outline" size="sm" onClick={validate} className="gap-1">
              <AlertCircle className="h-4 w-4" /> Validate
            </Button>
            <Button size="sm" onClick={() => saveMutation.mutate(quiz)} disabled={saveMutation.isPending} className="gap-1">
              <Save className="h-4 w-4" /> Save
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Quiz Settings */}
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Quiz Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <FieldRow label="Title *">
                  <Input
                    value={quiz.title}
                    onChange={e => updateQuiz({ title: e.target.value })}
                    placeholder="Quiz Night — August 2026"
                  />
                </FieldRow>
                <FieldRow label="Description">
                  <Textarea
                    value={quiz.description ?? ""}
                    onChange={e => updateQuiz({ description: e.target.value || null })}
                    rows={2}
                    placeholder="Brief description..."
                  />
                </FieldRow>
                <FieldRow label="Host Name">
                  <Input
                    value={quiz.hostName ?? ""}
                    onChange={e => updateQuiz({ hostName: e.target.value || null })}
                    placeholder="Ross"
                  />
                </FieldRow>
                <FieldRow label="Venue">
                  <Input
                    value={quiz.venue ?? ""}
                    onChange={e => updateQuiz({ venue: e.target.value || null })}
                    placeholder="The Kings Arms"
                  />
                </FieldRow>
                <FieldRow label="Scheduled Date / Time">
                  <Input
                    type="datetime-local"
                    value={quiz.scheduledAt ? quiz.scheduledAt.slice(0, 16) : ""}
                    onChange={e => updateQuiz({ scheduledAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  />
                </FieldRow>
                <FieldRow label="Join Instructions">
                  <Textarea
                    value={quiz.joinInstructions ?? ""}
                    onChange={e => updateQuiz({ joinInstructions: e.target.value || null })}
                    rows={2}
                    placeholder="How to join the quiz..."
                  />
                </FieldRow>
                <div className="grid grid-cols-2 gap-2">
                  <FieldRow label="Max Team Size">
                    <Input
                      type="number"
                      min={1}
                      value={quiz.maxTeamSize ?? ""}
                      onChange={e => updateQuiz({ maxTeamSize: e.target.value ? Number(e.target.value) : null })}
                      placeholder="6"
                    />
                  </FieldRow>
                  <FieldRow label="Max Teams">
                    <Input
                      type="number"
                      min={1}
                      value={quiz.maxTeams ?? ""}
                      onChange={e => updateQuiz({ maxTeams: e.target.value ? Number(e.target.value) : null })}
                      placeholder="Unlimited"
                    />
                  </FieldRow>
                </div>
                <FieldRow label="Default Timer">
                  <Select
                    value={quiz.defaultTimerSeconds.toString()}
                    onValueChange={v => updateQuiz({ defaultTimerSeconds: Number(v) })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIMER_OPTIONS.map(o => (
                        <SelectItem key={o.value} value={o.value.toString()}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldRow>
                <FieldRow label="Status">
                  <Select
                    value={quiz.status}
                    onValueChange={v => updateQuiz({ status: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["draft", "scheduled"].map(s => (
                        <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldRow>
                <div className="space-y-2 border-t pt-2">
                  <SwitchRow
                    label="Allow late joining"
                    checked={quiz.allowLateJoining}
                    onCheckedChange={v => updateQuiz({ allowLateJoining: v })}
                  />
                  <SwitchRow
                    label="Show scores between rounds"
                    checked={quiz.showScoresBetweenRounds}
                    onCheckedChange={v => updateQuiz({ showScoresBetweenRounds: v })}
                  />
                  <SwitchRow
                    label="Auto-lock answers"
                    checked={quiz.autoLockAnswers}
                    onCheckedChange={v => updateQuiz({ autoLockAnswers: v })}
                    description="Lock when timer expires"
                  />
                  <SwitchRow
                    label="Allow answer changes"
                    checked={quiz.allowAnswerChanges}
                    onCheckedChange={v => updateQuiz({ allowAnswerChanges: v })}
                    description="Until locked"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Center: Rounds & questions */}
          <div className="lg:col-span-1 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Rounds ({quiz.rounds.length})
              </h2>
              <Button size="sm" variant="outline" onClick={addRound} className="gap-1">
                <Plus className="h-3.5 w-3.5" /> Add Round
              </Button>
            </div>

            {quiz.rounds.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm border-2 border-dashed rounded-lg">
                No rounds yet. Click "Add Round" to start building.
              </div>
            )}

            {quiz.rounds.map((round, rIdx) => {
              const expanded = expandedRounds.has(rIdx);
              const isSelected = selectedRoundIdx === rIdx && selectedQuestionIdx === null;
              return (
                <Card key={rIdx} className={`overflow-hidden ${isSelected ? "ring-2 ring-primary" : ""}`}>
                  <div
                    className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      setExpandedRounds(prev => {
                        const s = new Set(prev);
                        if (s.has(rIdx)) s.delete(rIdx);
                        else s.add(rIdx);
                        return s;
                      });
                      setSelectedRoundIdx(rIdx);
                      setSelectedQuestionIdx(null);
                    }}
                  >
                    <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                    {expanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        Round {round.roundNumber}: {round.title}
                      </p>
                      <p className="text-xs text-muted-foreground">{round.questions.length} questions</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-400 hover:text-red-600"
                      onClick={e => { e.stopPropagation(); setDeleteRoundIdx(rIdx); }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {expanded && (
                    <div className="border-t px-3 py-2 space-y-1.5 bg-muted/20">
                      {/* Round settings inline */}
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div>
                          <Label className="text-xs">Title</Label>
                          <Input
                            value={round.title}
                            onChange={e => updateRound(rIdx, { title: e.target.value })}
                            className="h-7 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Default Timer</Label>
                          <Select
                            value={(round.defaultTimerSeconds ?? 30).toString()}
                            onValueChange={v => updateRound(rIdx, { defaultTimerSeconds: Number(v) })}
                          >
                            <SelectTrigger className="h-7 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {TIMER_OPTIONS.map(o => (
                                <SelectItem key={o.value} value={o.value.toString()}>{o.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {round.questions.map((q, qIdx) => {
                        const isQSelected = selectedRoundIdx === rIdx && selectedQuestionIdx === qIdx;
                        return (
                          <div
                            key={qIdx}
                            className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-background/80 ${isQSelected ? "bg-primary/10 ring-1 ring-primary" : "bg-background/50"}`}
                            onClick={() => { setSelectedRoundIdx(rIdx); setSelectedQuestionIdx(qIdx); }}
                          >
                            <span className="text-xs text-muted-foreground w-5 shrink-0">Q{q.questionNumber}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs truncate">{q.questionText || "(no text)"}</p>
                              <Badge variant="outline" className="text-[10px] h-4 mt-0.5">{q.type.replace(/_/g, " ")}</Badge>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-red-400 hover:text-red-600 shrink-0"
                              onClick={e => { e.stopPropagation(); setDeleteQuestionIdx({ roundIdx: rIdx, questionIdx: qIdx }); }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        );
                      })}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="w-full gap-1 text-xs"
                        onClick={() => addQuestion(rIdx)}
                      >
                        <Plus className="h-3 w-3" /> Add Question
                      </Button>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>

          {/* Right: Question editor */}
          <div className="lg:col-span-1">
            {selectedQuestion && selectedRoundIdx !== null && selectedQuestionIdx !== null ? (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">
                    Round {(selectedRound?.roundNumber ?? 0)} — Q{selectedQuestion.questionNumber}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <QuestionEditor
                    question={selectedQuestion}
                    onChange={q => updateQuestion(selectedRoundIdx, selectedQuestionIdx, q)}
                  />
                </CardContent>
              </Card>
            ) : selectedRound && selectedQuestionIdx === null && selectedRoundIdx !== null ? (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Round {selectedRound.roundNumber} Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <FieldRow label="Title">
                    <Input
                      value={selectedRound.title}
                      onChange={e => updateRound(selectedRoundIdx, { title: e.target.value })}
                    />
                  </FieldRow>
                  <FieldRow label="Description">
                    <Textarea
                      value={selectedRound.description ?? ""}
                      onChange={e => updateRound(selectedRoundIdx, { description: e.target.value || null })}
                      rows={2}
                    />
                  </FieldRow>
                  <FieldRow label="Default Points">
                    <Input
                      type="number"
                      min={1}
                      value={selectedRound.defaultPoints}
                      onChange={e => updateRound(selectedRoundIdx, { defaultPoints: Number(e.target.value) || 1 })}
                    />
                  </FieldRow>
                  <FieldRow label="Default Timer">
                    <Select
                      value={(selectedRound.defaultTimerSeconds ?? 30).toString()}
                      onValueChange={v => updateRound(selectedRoundIdx, { defaultTimerSeconds: Number(v) })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIMER_OPTIONS.map(o => (
                          <SelectItem key={o.value} value={o.value.toString()}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldRow>
                  <SwitchRow
                    label="Show leaderboard after round"
                    checked={selectedRound.showScores}
                    onCheckedChange={v => updateRound(selectedRoundIdx, { showScores: v })}
                  />
                </CardContent>
              </Card>
            ) : (
              <div className="flex items-center justify-center h-48 border-2 border-dashed rounded-lg text-muted-foreground text-sm">
                Select a round or question to edit
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete round confirm */}
      <AlertDialog open={deleteRoundIdx !== null} onOpenChange={open => !open && setDeleteRoundIdx(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Round?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the round and all its questions. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteRoundIdx !== null && deleteRound(deleteRoundIdx)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete question confirm */}
      <AlertDialog open={deleteQuestionIdx !== null} onOpenChange={open => !open && setDeleteQuestionIdx(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Question?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteQuestionIdx && deleteQuestion(deleteQuestionIdx.roundIdx, deleteQuestionIdx.questionIdx)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Validation dialog */}
      <AlertDialog open={showValidation} onOpenChange={setShowValidation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {validation.length === 0 ? "Quiz looks good!" : `${validation.length} issue(s) found`}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="max-h-60 overflow-y-auto space-y-2 px-1">
            {validation.length === 0 ? (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span>No issues found. Ready to run!</span>
              </div>
            ) : (
              validation.map((issue, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-2 text-sm p-2 rounded ${issue.type === "error" ? "bg-red-50 text-red-700" : "bg-yellow-50 text-yellow-700"}`}
                >
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{issue.message}</span>
                </div>
              ))
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowValidation(false)}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
